import yaml from 'js-yaml';
import { FilterValidationResult } from '../types/ai';

export const validateFilterYaml = (yamlContent: string): FilterValidationResult => {
  if (!yamlContent.trim()) {
    return { 
      isValid: false, 
      message: 'Filter configuration cannot be empty' 
    };
  }

  try {
    const parsed = yaml.load(yamlContent) as any;
    
    // Basic structure validation
    if (!parsed || typeof parsed !== 'object') {
      return { 
        isValid: false, 
        message: 'Filter must be a valid YAML object' 
      };
    }

    if (!parsed.filters || !Array.isArray(parsed.filters)) {
      return { 
        isValid: false, 
        message: 'Filter must contain a "filters" array' 
      };
    }

    if (parsed.filters.length === 0) {
      return { 
        isValid: false, 
        message: 'Filters array cannot be empty' 
      };
    }

    const warnings: string[] = [];

    // Validate each filter
    for (let i = 0; i < parsed.filters.length; i++) {
      const filter = parsed.filters[i];
      
      if (!filter || typeof filter !== 'object') {
        return { 
          isValid: false, 
          message: `Filter at index ${i} must be an object` 
        };
      }

      // Check if filter has either byConditions or byEventNames
      if (!filter.byConditions && !filter.byEventNames) {
        return { 
          isValid: false, 
          message: `Filter at index ${i} must have either "byConditions" or "byEventNames"` 
        };
      }

      // Validate byConditions
      if (filter.byConditions) {
        if (!filter.byConditions.action) {
          return { 
            isValid: false, 
            message: `Filter at index ${i} with byConditions must have an "action"` 
          };
        }

        if (!['allow', 'deny'].includes(filter.byConditions.action)) {
          return { 
            isValid: false, 
            message: `Filter at index ${i} action must be "allow" or "deny"` 
          };
        }

        if (!filter.byConditions.when) {
          return { 
            isValid: false, 
            message: `Filter at index ${i} with byConditions must have a "when" clause` 
          };
        }
      }

      // Validate byEventNames
      if (filter.byEventNames) {
        if (!filter.byEventNames.action) {
          return { 
            isValid: false, 
            message: `Filter at index ${i} with byEventNames must have an "action"` 
          };
        }

        if (!['allow', 'deny'].includes(filter.byEventNames.action)) {
          return { 
            isValid: false, 
            message: `Filter at index ${i} action must be "allow" or "deny"` 
          };
        }

        if (!filter.byEventNames.events || !Array.isArray(filter.byEventNames.events)) {
          return { 
            isValid: false, 
            message: `Filter at index ${i} with byEventNames must have an "events" array` 
          };
        }

        if (filter.byEventNames.events.length === 0) {
          warnings.push(`Filter at index ${i} has an empty events array`);
        }
      }
    }

    // Generate explanation
    let explanation = 'This filter configuration will ';
    const allowFilters = parsed.filters.filter((f: any) => 
      (f.byConditions && f.byConditions.action === 'allow') || 
      (f.byEventNames && f.byEventNames.action === 'allow')
    );
    const denyFilters = parsed.filters.filter((f: any) => 
      (f.byConditions && f.byConditions.action === 'deny') || 
      (f.byEventNames && f.byEventNames.action === 'deny')
    );

    if (allowFilters.length > 0 && denyFilters.length > 0) {
      explanation += `allow events that match ${allowFilters.length} condition(s) and deny events that match ${denyFilters.length} condition(s).`;
    } else if (allowFilters.length > 0) {
      explanation += `only allow events that match ${allowFilters.length} condition(s). All other events will be filtered out.`;
    } else if (denyFilters.length > 0) {
      explanation += `deny events that match ${denyFilters.length} condition(s). All other events will be allowed through.`;
    }

    return { 
      isValid: true, 
      message: 'Filter configuration is valid',
      explanation,
      warnings: warnings.length > 0 ? warnings : undefined
    };

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown parsing error';
    return { 
      isValid: false, 
      message: `YAML parsing error: ${errorMessage}` 
    };
  }
};

export const generateFilterExplanation = (yamlContent: string): string => {
  try {
    const parsed = yaml.load(yamlContent) as any;
    if (!parsed?.filters) return 'Invalid filter configuration';

    const explanations: string[] = [];
    
    parsed.filters.forEach((filter: any) => {
      if (filter.byConditions) {
        const action = filter.byConditions.action === 'allow' ? 'Allow' : 'Block';
        explanations.push(`${action} events based on conditions`);
      } else if (filter.byEventNames) {
        const action = filter.byEventNames.action === 'allow' ? 'Allow only' : 'Block';
        const eventCount = filter.byEventNames.events?.length || 0;
        explanations.push(`${action} ${eventCount} specific event type(s)`);
      }
    });

    return explanations.join('; ');
  } catch {
    return 'Unable to parse filter configuration';
  }
};
